import { useState, useEffect, useMemo } from 'react';
import NewProductModal from './NewProductModal';
import { useNavigation } from '../context/NavigationContext';
import { useUI } from '../context/UIContext';
import { useAuth } from '../context/AuthContext';
import { useQueryClient } from '@tanstack/react-query';
import Swal from 'sweetalert2';

import { useProducts } from '../hooks/products/useProducts';
import { useCompanies } from '@/api/queries';

import StandardHeader from './layout/StandardHeader';
import { Plus } from 'lucide-react';
import ProductStats from './produtos/ProductStats';
import ProductActionBar from './produtos/ProductActionBar';
import TimelineView, { TimelineItem } from './common/TimelineView';
import BoardToolbar from './common/management/BoardToolbar';
import BoardKanban, { KanbanColumn, KanbanItem } from './common/management/BoardKanban';
import BoardList from './common/management/BoardList';
import { productTypeConfig, PRODUCT_TYPES } from '../utils/productConfig';
import apiClient from '../api/client';
import { ProductGalleryService } from '../services/products/products.service';

const STATUS_COLUMNS: KanbanColumn[] = [
  { id: 'Planejamento', label: 'Planejamento', badge: 'bg-neutral-50 text-neutral-500 border border-neutral-200/50' },
  { id: 'Em Andamento', label: 'Em Andamento', badge: 'bg-orange-50 text-orange-800 border border-orange-200/50' },
  { id: 'Beta', label: 'Beta', badge: 'bg-blue-50 text-blue-800 border border-blue-200/50' },
  { id: 'Concluído', label: 'Concluído', badge: 'bg-emerald-50 text-emerald-800 border border-emerald-200/30' },
];

const mapBackendStatusToColumn = (status?: string) => {
  const s = (status || '').toString().trim().toLowerCase();
  if (!s || s === 'planejamento' || s === 'planned') return 'Planejamento';
  if (s.includes('andamento') || s === 'ativo' || s === 'active' || s === 'em andamento') return 'Em Andamento';
  if (s.includes('beta')) return 'Beta';
  if (s.includes('conclu') || s.includes('completed') || s.includes('cancel') || s === 'concluído') return 'Concluído';
  return 'Planejamento';
};

export default function ProdutosView() {
  const { globalFilters, setGlobalFilters } = useNavigation();
  const { setCurrentView } = useUI();
  
  const qc = useQueryClient();

  const { data: productsData = [], isLoading: productsLoading } = useProducts();
  const { data: companiesData = [] } = useCompanies();

  const [products, setProducts] = useState<any[]>([]);
  const [companies, setCompanies] = useState<any[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [companyFilter, setCompanyFilter] = useState('ALL');
  const [viewMode, setViewMode] = useState<'kanban' | 'list' | 'timeline' | 'gantt'>('kanban');
  const [isNewModalOpen, setIsNewModalOpen] = useState(false);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  useEffect(() => { setProducts(productsData); }, [productsData]);
  useEffect(() => { setCompanies(companiesData as any); }, [companiesData]);

  useEffect(() => {
    if (globalFilters.companyId && companies.length > 0) {
      const company = companies.find(c => c.id.toString() === globalFilters.companyId?.toString());
      if (company) setCompanyFilter(company.name);
    } else {
      setCompanyFilter('ALL');
    }
  }, [globalFilters.companyId, companies]);

  const handleProductClick = (p: any) => {
    setGlobalFilters((prev: any) => ({ ...prev, productId: p.id }));
    setCurrentView('produto-workspace');
  };

  const refreshProducts = async () => {
    await qc.invalidateQueries({ queryKey: ['products', 'list'] });
  };

  const handleUpdateProductDates = async (productId: string | number, newStartDate: string, newEndDate: string) => {
    const idStr = String(productId);
    const prod = products.find(p => String(p.id) === idStr);
    if (!prod) return;
    const tags = Array.isArray(prod.tags) ? prod.tags : [];
    const updatedTags = [...tags.filter((t: string) => !t.startsWith('start:') && !t.startsWith('end:')), `start:${newStartDate}`, `end:${newEndDate}`];
    try {
      const { data } = await apiClient.put(`/api/products/${idStr}`, { tags: updatedTags });
      const real = data?.data || data || { tags: updatedTags };
      setProducts(prev => prev.map(p => String(p.id) === idStr ? { ...p, ...real } : p));
      await refreshProducts();
    } catch (e) {
      console.error('Error updating dates:', e);
    }
  };

  const handleDropKanban = async (e: React.DragEvent, colId: string) => {
    e.preventDefault();
    const raw = (window as any).__draggedItemId || e.dataTransfer.getData('itemId') || e.dataTransfer.getData('text/plain');
    const prodId = String(raw || '').trim();
    (window as any).__draggedItemId = null;
    if (!prodId) return;

    const newStatus = mapBackendStatusToColumn(colId);
    const snapshot = products.find(p => String(p.id) === prodId);

    setProducts(prev => prev.map(i => String(i.id) === prodId ? { ...i, status: newStatus } : i));

    try {
      const { data } = await apiClient.put(`/api/products/${prodId}`, { status: newStatus });
      const real = data?.data || data;
      if (real && snapshot) {
        setProducts(prev => prev.map(i => String(i.id) === prodId ? { ...snapshot, ...real } : i));
      }
      Swal.fire({ icon: 'success', title: 'Produto atualizado!', toast: true, position: 'top-end', showConfirmButton: false, timer: 2000 });
    } catch (err) {
      console.error(err);
      if (snapshot) setProducts([...products]);
      Swal.fire({ icon: 'error', title: 'Erro ao atualizar.', toast: true, position: 'top-end', timer: 3000 });
    }
  };

  const handleAddProduct = async (payload: { name: string; description: string; status: string; companyId?: string | number; type?: string; imageFiles?: File[] }) => {
    const optimisticItem = {
      id: `temp-${Date.now()}`,
      name: payload.name,
      description: payload.description || 'Novo produto adicionado ao catálogo.',
      status: mapBackendStatusToColumn(payload.status),
      empresa: 'Empresa Interna',
      companyName: 'Empresa Interna',
      type: payload.type || 'digital',
      tags: [],
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };

    const previousList = products;
    setProducts([optimisticItem, ...products]);

    try {
      const { data } = await apiClient.post('/api/products', {
        name: payload.name,
        description: payload.description,
        type: payload.type || 'digital',
        status: mapBackendStatusToColumn(payload.status),
        tags: [],
      });
      const realProduct = data?.data || data;
      const realId = realProduct?.id || optimisticItem.id;
      setProducts(prev => prev.map(p => p.id === optimisticItem.id ? { ...p, ...realProduct, id: realId } : p));
      if (realProduct?.id && payload.imageFiles?.length) {
        for (const [index, file] of payload.imageFiles.entries()) {
          await ProductGalleryService.upload(realProduct.id, file, { alt: file.name, isPrimary: index === 0 });
        }
      }
      await refreshProducts();
      Swal.fire({ icon: 'success', title: 'Produto criado!', toast: true, position: 'top-end', showConfirmButton: false, timer: 2000 });
    } catch (e) {
      console.error(e);
      setProducts(previousList);
      Swal.fire({ icon: 'error', title: 'Erro de conexão com o banco de dados.', toast: true, position: 'top-end', timer: 5000 });
    }
  };

  const handleDeleteProduct = async (productId: string | number) => {
    if (!confirm('Deseja realmente excluir este produto?')) return;
    const snapshot = products.find(p => String(p.id) === String(productId));
    setProducts(prev => prev.filter(p => String(p.id) !== String(productId)));
    try {
      await apiClient.delete(`/api/products/${String(productId)}`);
      await refreshProducts();
      Swal.fire({ icon: 'success', title: 'Produto excluído!', toast: true, position: 'top-end', showConfirmButton: false, timer: 2000 });
    } catch (e) {
      console.error(e);
      if (snapshot) setProducts([...products]);
    }
  };

  const mappedProducts = useMemo(() => {
    return products.map(p => {
      const company = companies.find(c => String(c.id) === String(p.companyId));
      return {
        ...p,
        empresa: p.empresa || (company ? company.name : 'Empresa Interna'),
        companyName: p.companyName || (company ? company.name : 'Empresa Interna'),
        desc: p.description,
        revenue: 'R$ 0,00',
        logo: p.logo || (p.name ? p.name.charAt(0).toUpperCase() : 'P'),
        updated: new Date(p.updatedAt || p.createdAt || Date.now()).toLocaleDateString('pt-BR'),
      };
    });
  }, [products, companies]);

  const filteredProducts = useMemo(() => mappedProducts.filter(p => {
    const matchesSearch = p.name?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === 'ALL' || mapBackendStatusToColumn(p.status) === statusFilter;
    const matchesCompany = companyFilter === 'ALL' || p.empresa === companyFilter;
    return matchesSearch && matchesStatus && matchesCompany;
  }), [mappedProducts, searchQuery, statusFilter, companyFilter]);

  const timelineItems = useMemo<TimelineItem[]>(() => {
    return filteredProducts.map(prod => {
      let startDate = '';
      let endDate = '';
      const tags = Array.isArray(prod.tags) ? prod.tags : [];
      const startTag = tags.find((t: string) => t.startsWith('start:'));
      const endTag = tags.find((t: string) => t.startsWith('end:'));
      if (startTag) startDate = startTag.replace('start:', '').split('T')[0];
      if (endTag) endDate = endTag.replace('end:', '').split('T')[0];
      if (!endDate && startDate) {
        const d = new Date(startDate);
        d.setDate(d.getDate() + 30);
        endDate = d.toISOString().split('T')[0];
      }
      return {
        id: prod.id,
        name: prod.name,
        startDate: startDate || new Date().toISOString().split('T')[0],
        endDate: endDate || new Date(Date.now() + 2592000000).toISOString().split('T')[0],
        status: prod.status || 'Planejamento',
        statusLabel: prod.status || 'Planejamento',
        priority: 'Média',
        assignee: prod.empresa || '-',
        progress: 30,
        dependencies: [],
        rawItem: prod,
      };
    });
  }, [filteredProducts]);

  const kanbanItems: KanbanItem[] = useMemo(() => {
    return filteredProducts.map(i => {
      const colId = i.status || 'Planejamento';
      const badges: { text: string; colorClass?: string }[] = [];
      if (i.type && productTypeConfig[i.type as keyof typeof productTypeConfig]) {
        const config = productTypeConfig[i.type as keyof typeof productTypeConfig];
        badges.push({ text: config.name, colorClass: config.badgeColor });
      } else {
        const config = productTypeConfig[PRODUCT_TYPES.DIGITAL];
        badges.push({ text: config.name, colorClass: config.badgeColor });
      }
      return {
        id: i.id,
        title: i.name,
        subtitle: i.empresa || 'Empresa',
        owner: i.empresa || '-',
        priority: 'Média',
        progress: 30,
        status: colId,
        raw: i,
        badges,
        customMetrics: [
          { label: 'Receita / MRR', value: 'R$ 0,00', colorClass: 'text-emerald-600' },
          { label: 'Projetos Relac.', value: 0, colorClass: 'text-neutral-900' },
        ],
      };
    });
  }, [filteredProducts]);

  const activeCount = products.filter(p => p.status === 'Concluído' || p.status === 'Concluido').length;
  const devCount = products.filter(p => p.status === 'Em Andamento' || p.status === 'Em Desenvolvimento').length;

  if (productsLoading && products.length === 0) {
    return <div className="p-10 text-center text-neutral-400 text-sm font-bold">Carregando produtos...</div>;
  }

  return (
    <div className="w-full mx-auto pb-12 flex flex-col gap-10 animate-in fade-in duration-500 relative px-4 sm:px-6 lg:px-10">
      <StandardHeader
        title="Produtos"
        subtitle="Gerencie o portfólio de softwares, plataformas e soluções digitais do ecossistema."
        actions={[
          {
            label: 'Novo Produto',
            icon: Plus,
            onClick: () => setIsNewModalOpen(true),
            variant: 'primary',
          },
        ]}
      />

      <ProductStats totalProducts={products.length} activeProducts={activeCount} devProducts={devCount} totalProjects={0} />

      <main className="w-full flex flex-col gap-5">
        <BoardToolbar
          innerSearch={searchQuery}
          setInnerSearch={setSearchQuery}
          viewMode={viewMode as any}
          setViewMode={setViewMode as any}
          statusFilter={statusFilter}
          setStatusFilter={setStatusFilter}
          clientFilter={companyFilter}
          setClientFilter={setCompanyFilter}
          clients={companies}
        />

        <div className="w-full overflow-hidden">
          {viewMode === 'kanban' && (
            <BoardKanban
              columns={STATUS_COLUMNS}
              items={kanbanItems}
              onDrop={handleDropKanban}
              onItemClick={handleProductClick}
              onAddClick={() => setIsNewModalOpen(true)}
              emptyMessage="Nenhum produto nesta etapa."
              disableLayoutAnimation={true}
            />
          )}

          {viewMode === 'list' && (
            <BoardList
              columns={[
                { key: 'title', label: 'Produto' },
                { key: 'empresa', label: 'Cliente' },
                { key: 'type', label: 'Tipo' },
                { key: 'status', label: 'Status' },
              ]}
              items={filteredProducts}
              onItemClick={handleProductClick}
              renderCell={(item, colKey) => {
                if (colKey === 'title') return <div className="font-bold text-neutral-900">{item.name}</div>;
                if (colKey === 'empresa') return <div className="text-slate-600 font-semibold">{item.empresa || '-'}</div>;
                if (colKey === 'type') {
                  const config = item.type && productTypeConfig[item.type as keyof typeof productTypeConfig]
                    ? productTypeConfig[item.type as keyof typeof productTypeConfig]
                    : productTypeConfig[PRODUCT_TYPES.DIGITAL];
                  const Icon = config.icon;
                  return (
                    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-bold border ${config.badgeColor}`}>
                      <Icon size={12} strokeWidth={2.5} />
                      {config.name}
                    </span>
                  );
                }
                if (colKey === 'status') {
                  const col = STATUS_COLUMNS.find(c => c.id === item.status);
                  return <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold ${col?.badge || 'bg-slate-100 text-slate-600'}`}>{col?.label || item.status}</span>;
                }
                return null;
              }}
            />
          )}

          {(viewMode === 'timeline' || viewMode === 'gantt') && (
            <div className="w-full bg-white p-6 rounded-[24px] border border-slate-200 shadow-sm">
              <TimelineView
                items={timelineItems}
                onUpdateItemDates={handleUpdateProductDates}
                onItemClick={(rawItem) => handleProductClick(rawItem)}
                onDeleteItem={(productId) => {
                  setProducts(prev => prev.filter(p => String(p.id) !== String(productId)));
                }}
                title="Linha do Tempo dos Produtos"
                emptyMessage="Nenhum produto cadastrado para exibir na linha do tempo."
              />
            </div>
          )}
        </div>
      </main>

      <ProductActionBar selectedCount={selectedIds.length} onClear={() => setSelectedIds([])} />
      <NewProductModal isOpen={isNewModalOpen} onClose={() => setIsNewModalOpen(false)} onSave={handleAddProduct} companies={companies} />
    </div>
  );
}
